import { Plus } from "lucide-react";
import type { List, ListMemberRef } from "@guestbook/shared";
import { Button } from "@/shared/components/ui/Button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/shared/components/ui/DropdownMenu";
import { useAddListMembers, useLists } from "@/shared/lib/lists";

interface AddToListMenuProps {
  selectedIds: string[];
  onDone: () => void;
}

/**
 * Bulk action over a contacts selection: pick a list, add the selected
 * contacts to it. The mutation is built fresh for each pick (each list has
 * its own POST /lists/:id/members endpoint), so the mutation hook is
 * instantiated per item. Empty `lists.data` shows a single disabled item.
 */
export function AddToListMenu({ selectedIds, onDone }: AddToListMenuProps) {
  const lists = useLists();
  const items = lists.data?.items ?? [];

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          disabled={selectedIds.length === 0}
          aria-label="Add to list"
        >
          <Plus aria-hidden className="h-4 w-4" />
          <span className="hidden sm:inline">Add to list</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="center" sideOffset={20} className="min-w-[14rem]">
        <DropdownMenuLabel className="px-3 py-1.5 type-label-sm tracking-wider uppercase text-text-muted">
          Your lists
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {lists.isLoading ? (
          <DropdownMenuItem disabled>Loading…</DropdownMenuItem>
        ) : items.length === 0 ? (
          <DropdownMenuItem disabled>
            No lists yet — create one from the header.
          </DropdownMenuItem>
        ) : (
          items.map((list) => (
            <AddToListItem
              key={list.id}
              list={list}
              selectedIds={selectedIds}
              onDone={onDone}
            />
          ))
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

interface AddToListItemProps {
  list: List;
  selectedIds: string[];
  onDone: () => void;
}

function AddToListItem({ list, selectedIds, onDone }: AddToListItemProps) {
  const add = useAddListMembers(list.id);
  function handleSelect() {
    const members: ListMemberRef[] = selectedIds.map((id) => ({ contactId: id }));
    add.mutate(
      { members },
      {
        onSuccess: () => onDone(),
      },
    );
  }
  return (
    <DropdownMenuItem
      onSelect={(e) => {
        // Keep the menu open while the mutation flies so error states have
        // somewhere to render; close happens via onDone on success.
        e.preventDefault();
        handleSelect();
      }}
      disabled={add.isPending}
    >
      <span className="flex-1 truncate">{list.name}</span>
      <span className="type-body-sm text-text-muted">
        {add.isPending ? "Adding…" : list.memberCount}
      </span>
    </DropdownMenuItem>
  );
}
